<template>
  <detail-layout-full-width>
    <template slot="header">
      <base-header
        icon="user"
        :content="header"
      />
    </template>
    <template slot="main">
      <div class="form-wrapper">
        <el-form
          :model="form"
          :rules="rules"
          ref="ruleForm"
        >
        <div class="lg:max-w-detail lg:mr-auto">
          <div class="mb-4 mt-4">
            <label for="switch-personalizado" class="text-sm font-semibold text-secondary-600 block mb-1 flex flex-col">
              {{$t('clients.representative_default')}}
              <span class="sr-only">{{$t('app.options')}}: {{$t('smtp.default')}} / {{$t('smtp.custom')}}</span>
            </label>
            <el-switch 
              id="switch-personalizado"
              :aria-label="$t('clients.representative_default')"
              class="switch-text break-normal both-switch mt-2" 
              v-model="form.Personalizado" 
              :active-text="$t('smtp.custom')"
              :inactive-text="$t('smtp.default')"
              inactive-color="var(--primary-800)"
              />
          </div>
          <div class="lg:mb-4 mt-4" v-if="form.Personalizado">
            <div class="mandatory">
              <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('clients.representative_name')}}
                <el-form-item prop="Nombre">
                  <el-input
                    :disabled="!form.Personalizado"
                    clearable
                    class="w-full"
                    v-model="form.Nombre"
                    maxlength='100'
                  />
                </el-form-item>
              </label>
            </div>
          </div>
          <div class="lg:mb-2" v-if="form.Personalizado">
            <div class="mandatory">
              <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('clients.representative_nif')}}
                <el-form-item prop="NIF">
                  <el-input
                    :disabled="!form.Personalizado"
                    clearable
                    class="w-full"
                    v-model="form.NIF"
                    maxlength='9'
                  />
                </el-form-item>
              </label>
            </div>
          </div>

          <div class="lg:mb-2" v-if="form.Personalizado">
            <div class="mandatory">
              <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('clients.representative_phone')}}
                <el-form-item prop="Telefono">
                  <el-input
                    :disabled="!form.Personalizado"
                    clearable
                    class="w-full"
                    v-model="form.Telefono"
                    maxlength='9'
                  />
                </el-form-item>
              </label>
            </div>
          </div>

          <div class="lg:mb-2" v-if="form.Personalizado">
            <AccessibleElSelect
              v-model="form.Email"
              prop="Email"
              :options="emailsUsuariosForAccessibleSelect"
              :isDisabled="!form.Personalizado"
              clearable
              id-prefix="representative"
            >
              <template slot="label">{{$t('clients.representative_email')}}</template>
            </AccessibleElSelect>
          </div>
        </div>
        </el-form>
      </div>
      </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive mode="secondary" :text="isFromClient ? $t('app.cancel') : $t('app.back')" icon="cross" @click="goBack" :loading="isGeneralLoading"/>
          <base-button-responsive mode="primary" :text="$t('app.save')" icon="save" @click="submitForm('ruleForm')" :loading="isGeneralLoading" :disabled="isGeneralLoading"/>
        </template>
      </base-footer>
    </template>
  </detail-layout-full-width>
</template>

<script>
import DetailLayoutFullWidth from '@/components/layout/DetailLayoutFullWidth.vue'
import AccessibleElSelect from '@/components/elementui/AccessibleElSelect.vue'
import { getRepresentativeData } from '@/services/clients/getRepresentativeData'
import { getUsersEmails } from '@/services/configurations/getUsersEmails'
import { updateRepresentativeData } from '@/services/clients/updateRepresentativeData'
import { getClient } from '@/services/clients/getClient'
import { i18n } from '@/i18n'
import { email as emailRegex } from '@/utils/regex'

function isValidEmail (value) {
  return emailRegex.test(value)
}

function isPhoneValid (value) {
  var onlyNumbers = /^\d+$/
  return onlyNumbers.test(value)
}
export default {
  /* eslint-disable camelcase */
  name: 'data-representative-detail',
  components: {
    DetailLayoutFullWidth,
    AccessibleElSelect,
  },
  data: function () {
    return {
      activeName: 'es',
      header: {
        title: i18n.t('clients.representative_header_text'),
        icon: 'config',
        breadcrumb: {},
        nav: [],
        url: this.isFromClient ? 'configurations/clients/details' : 'configurations',
        tooltip: i18n.t('clients.representative_header_tooltip')
      },
      emailsUsuarios: [],
      form: {
        Personalizado: true,
        Nombre: null,
        NIF: null,
        Telefono: null,
        Email: null,
      },
      rules: {
        Nombre: [
          { validator: this.isGenericValidation, trigger: 'blur' },
        ],
        NIF: [
          { validator: this.isGenericValidation, trigger: 'blur' },
        ],
        Telefono: [
          { validator: this.isGenericValidation, trigger: 'blur' },
          { required: true, validator: this.isPhoneValid, trigger: 'blur' }
        ],
        Email: [
          { validator: this.isGenericValidation, trigger: 'blur' },
          { validator: this.isGenericValidation, trigger: 'change' },
          { required: true, validator: this.isEmailValid, trigger: 'blur' }
        ],
      }
    }
  },
  computed: {
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    role: function () {
      return this.$store.getters['auth/role']
    },
    id () {
      return this.$route.params.id ? this.$route.params.id : -1
    },
    isFromClient () {
      return this.id !== -1
    },
    allFormFieldsEmpty () {
      return !this.form.Nombre && !this.form.NIF && !this.form.Telefono && !this.form.Email
    },
    emailsUsuariosForAccessibleSelect () {
      return this.emailsUsuarios.map(email => ({
        id: email,
        value: email,
        name: email
      }))
    },
  },
  watch: {
    form: {
      deep: true,
      immediate: true,
      handler: 'watchFormHandler'
    }
  },
  created: async function () {
    const breadcrumbs = []

    if (this.isFromClient) {
      breadcrumbs.push({
        path: this.role === 'SuperAdministrador' ? 'configurationssa' : 'configurations',
        name: this.$i18n.t('app.configurations')
      })
      breadcrumbs.push({
        path: 'configurations/clients',
        name: this.$i18n.t('app.clients')
      })
      if (this.$store.getters['app/fromRouteName'] !== null) {
        const breadcrumbHeader = this.$store.getters['app/headerBreadCrumb']
        breadcrumbs.push({ path: '', name: breadcrumbHeader })
      } else {
        const cliente = await this.downloadClientData(this.id)
        breadcrumbs.push({ path: '', name: cliente.empresa.Nombre })
        this.$store.commit('app/setheaderBreadCrumb', cliente.empresa.Nombre)
      }
    }

    this.header.breadcrumb = breadcrumbs

    await this.downloadEmailsUsers()
    const data = await this.downloadData(this.id)
    this.setData(data.respuesta)
  },
  mounted () {
    this.header.url = this.isFromClient ? 'configurations/clients/details' : 'configurations'
    this.resetControlFormChanges()
  },
  methods: {
    downloadClientData: async function (id) {
      let client = null
      try {
        this.$store.commit('setIsLoading', true)
        const params = {
          idEmpresa: id
        }
        client = await getClient(params)
        if (client.blnError === true) {
          this.doFeedback(client.msgError + ' [Código de error: ' + client.codError + ']')
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
      return client
    },
    watchFormHandler () {
      this.$store.commit('app/setFormChanges', true)
    },
    isEmailValid: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!isValidEmail(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.email'), callback)
      } else {
        callback()
      }
    },
    isPhoneValid: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (value.length !== 9 || !isPhoneValid(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.phone.length_numbers'), callback)
      } else {
        callback()
      }
    },
    submitForm: function (formName) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      this.$refs[formName].validate(async valid => {
        if (valid || (this.form.Personalizado && this.allFormFieldsEmpty)) {
          if (this.form.Personalizado && this.allFormFieldsEmpty) {
            this.$refs.ruleForm.resetFields()
          }
          const payload = {
            Personalizado: this.form.Personalizado,
            Nombre: this.form.Nombre,
            NIF: this.form.NIF,
            Telefono: this.form.Telefono,
            Email: this.form.Email,
          }
          const response = await updateRepresentativeData({ idEmpresa: this.id }, payload)
          if (response.blnError === true) {
            this.doFeedback(response.msgError + ' [Código de error: ' + response.codError + ']')
          } else {
            this.doFeedback('Datos actualizados correctamente', 'success')
            this.resetControlFormChanges()
          }
        } else {
          this.doFeedback('Error al procesar los datos, revise la información')
        }
        this.$store.commit('setIsLoading', false)
      })
    },
    goBack: function () {
      if (this.isFromClient) this.$router.push({ name: 'configurations/clients/details', params: this.id })
      else this.$router.push({ name: 'companies' })
    },
    downloadEmailsUsers: async function () {
      try {
        this.$store.commit('setIsLoading', true)
        const emails = await getUsersEmails({ idCliente: this.role === 'Administrador' ? null : this.id })
        if (emails.blnError === true) {
          this.doFeedback(emails.msgError + ' [Código de error: ' + emails.codError + ']')
        } else {
          this.emailsUsuarios = emails.Emails
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    downloadData: async function () {
      try {
        this.$store.commit('setIsLoading', true)
        const template = await getRepresentativeData({ idEmpresa: this.id })
        if (template.blnError === true) {
          this.doFeedback(template.msgError + ' [Código de error: ' + template.codError + ']')
        } else {
          return template
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
      return null
    },
    setData: function (data) {
      if (data) {
        this.form.Personalizado = data.Personalizado ? data.Personalizado : false
        this.form.Nombre = data.Nombre ? data.Nombre : null
        this.form.NIF = data.NIF ? data.NIF : null
        this.form.Telefono = data.Telefono ? data.Telefono : null
        this.form.Email = data.Email ? data.Email : null
      }
      this.resetControlFormChanges()
    }
  }

}
</script>

<style scoped>
.box {
  background-color: #444;
  color: #fff;
  border-radius: 5px;
  border: 1px solid black;
  padding: 5px 20px;
}
.grid {
  display: grid;
  grid-gap: 10px;
}
.wrapper {
  grid-template-columns: 1fr 1fr ;
  background-color: seagreen;
  padding: 10px;
  width: 50%;
  margin: 20px auto;
}
.grid.left {
  grid-column: 1 / span 1;
  grid-auto-rows: min-content;
}
.grid.right {
  grid-column: span 1 / -1;
  grid-auto-rows: min-content;
}
.tip {
  grid-column: 1 / -1;
}
.pic>img {
  max-width: 100%;
}

</style>

<style lang="sass" scoped>
</style>
